import { useCallback, useState } from "react";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Typography from "components/elements/Typography";
import CreateProjectModal from "components/materials/CreateProjectModal";
import I18n from "components/materials/I18n";
import UpgradeToProButton from "components/materials/UpgradeToProButton";
import { useUserSubscription } from "hooks/useUserSubscription";
import classes from "./classes.module.scss";

export enum ETab {
	ALL = "ALL",
	ONLINE = "ONLINE",
	DRAFT = "DRAFT",
	ARCHIVED = "ARCHIVED",
}
type IButtonTabProps = {
	tab: ETab;
	onClick: (tab: ETab) => void;
	isActiveTab: (tab: ETab) => boolean;
	getTabTitle: (tab: ETab) => string;
	disabled: boolean;
};
type IProps = {
	onTabChange: (tab: ETab) => void;
};

export default function DashboardHeader(props: IProps) {
	const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
	const { getTabTitle, isActiveTab, onButtonTabClick } = useButtonTab();
	const { isUserSubscribed } = useUserSubscription();

	const onClick = useCallback(
		(tab: ETab) => {
			props.onTabChange(tab);
			onButtonTabClick(tab);
		},
		[props, onButtonTabClick],
	);

	const toggleCreateProjectModal = useCallback(() => setIsCreateProjectModalOpen((prev) => !prev), []);

	return (
		<div className={classes["root"]}>
			<div className={classes["filters-button"]}>
				{Object.values(ETab).map((tab) => (
					<ButtonTab key={tab} tab={tab} onClick={onClick} isActiveTab={isActiveTab} getTabTitle={getTabTitle} disabled={!isUserSubscribed} />
				))}
			</div>

			<div className={classes["project-button"]}>
				<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} onClick={toggleCreateProjectModal} disabled={!isUserSubscribed}>
					<Typography type="p" size="medium" weight="medium">
						{I18n.asset.pages.my_projects.dashboard_header.create_project}
					</Typography>
				</Button>
				<UpgradeToProButton />
			</div>

			<CreateProjectModal isOpen={isCreateProjectModalOpen} onClose={toggleCreateProjectModal} />
		</div>
	);
}

function ButtonTab(props: IButtonTabProps) {
	const onClick = useCallback(() => props.onClick(props.tab), [props]);

	return (
		<Button variant={EButtonVariant.SELECTOR} onClick={onClick} isSelected={props.isActiveTab(props.tab)} disabled={props.disabled}>
			<Typography type="p" size="medium" weight="bold" typoFont={props.isActiveTab(props.tab) ? "primary" : "secondary"}>
				{props.getTabTitle(props.tab)}
			</Typography>
		</Button>
	);
}

const useButtonTab = () => {
	const [activeTab, setActiveTab] = useState<ETab>(ETab.ALL);

	const getTabTitle = useCallback((tab: ETab) => {
		switch (tab) {
			case ETab.ALL:
				return I18n.asset.pages.my_projects.dashboard_header.all;
			case ETab.ONLINE:
				return I18n.asset.pages.my_projects.dashboard_header.online;
			case ETab.DRAFT:
				return I18n.asset.pages.my_projects.dashboard_header.draft;
			case ETab.ARCHIVED:
				return I18n.asset.pages.my_projects.dashboard_header.archived;
		}
	}, []);

	const isActiveTab = useCallback((tab: ETab) => activeTab === tab, [activeTab]);

	const onButtonTabClick = useCallback((tab: ETab) => setActiveTab(tab), []);

	return { getTabTitle, isActiveTab, onButtonTabClick };
};
